import React from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Catches lazy-chunk / render failures so navigation never leaves a blank screen.
 * Resets when the path changes so a later route can recover without a full reload.
 */
class RouteErrorBoundaryInner extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('Route failed to render:', error, info);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const label = this.props.label || 'this page';
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            minHeight: '60vh',
            padding: '24px',
            fontFamily: 'ui-monospace, monospace',
            fontSize: '0.9rem',
            color: '#0a0a0a',
            textAlign: 'center',
          }}
          role="alert"
        >
          <p style={{ margin: 0 }}>
            Failed to load {label}. Try again, or refresh if it keeps happening.
          </p>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={this.handleRetry}
              style={{
                cursor: 'pointer',
                padding: '8px 14px',
                border: '2px solid #0a0a0a',
                background: '#a8d5ba',
                fontFamily: 'inherit',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Try again
            </button>
            <button
              type="button"
              onClick={this.handleReload}
              style={{
                cursor: 'pointer',
                padding: '8px 14px',
                border: '2px solid #0a0a0a',
                background: '#fff',
                fontFamily: 'inherit',
                fontSize: '0.75rem',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Refresh
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/** Hook wrapper so the boundary resets on every navigation. */
const RouteErrorBoundary = ({ children, label }) => {
  const location = useLocation();
  return (
    <RouteErrorBoundaryInner resetKey={location.pathname} label={label}>
      {children}
    </RouteErrorBoundaryInner>
  );
};

export default RouteErrorBoundary;
